<div class="fd-shell">
<w-page [mode]="PageMode.Page" [headerSize]="PageHeaderSize.Small" [hideFooter]="true" [forceShowClose]="$showClose()" (close)="onClose()" [class.is-locked]="$readonly()" [attr.data-rail-layout]="$railLayout()" class="frame-financial-document" styleClass="surface-financial-document-frame">

  <ng-container *w-page-header-title>
    <div class="fd-title-row">
      <h2 class="fd-title">{{ $title() }}</h2>
      @for (tag of $headerTags(); track tag.key) {
        @if (tag.style) {
          <w-badge [label]="tag.label" mode="squared" [color]="tag.style.color" [backgroundColor]="tag.style.backgroundColor" [borderColor]="tag.style.borderColor" />
        } @else {
          <w-badge [label]="tag.label" mode="squared" />
        }
      }
      @for (item of $headerSlot(); track item.key) {
        <ng-container *ngComponentOutlet="item.component" />
      }
    </div>
  </ng-container>

  <ng-container *w-page-header-actions>
    <wb-action-bar [commands]="$actionCommands()" />
    <!-- Last, so capability controls sit beside w-page's close. -->
    <wb-surface-capability-region [fields]="$capabilityFields()" [surfaceMode]="$surfaceMode()" />
  </ng-container>

  <!-- Banner region under the header (alert strips). Mounted from the optional
       `banner` capability; absent → nothing renders. -->
  @if ($banner(); as banner) {
    <ng-container *w-page-alert>
      <ng-container [ngComponentOutlet]="banner.component" [ngComponentOutletInputs]="banner.inputs?.() ?? {}" />
    </ng-container>
  }

  <ng-container *w-page-body-content>
    @if ($readonly()) {
      <w-doc-zoom-frame [docWidth]="documentWidth" [fillHeight]="true" [flushScrollbarRight]="true" [paddingX]="56" [paddingY]="24" initialFit="fit-width" [seed]="zoomSeed" [layoutKey]="$railLayout()" (stateChange)="onZoomStateChange($event)">
        <div class="fd-document-page">
          <ng-container *ngTemplateOutlet="documentContent" />
        </div>
      </w-doc-zoom-frame>
    } @else {
      <ng-container *ngTemplateOutlet="documentContent" />
    }
  </ng-container>

  <ng-container *w-page-body-right-content>
    @if ($showRail()) {
      <wb-surface-rail [tabs]="$railTabs()" [collapsed]="$railCollapsed()" (collapsedChange)="railState.setCollapsed($event)" [activeTab]="$activeRailTab()" (activeTabChange)="onRailTabChange($event)" [pinAvailable]="$railDockMode()" [pinned]="$railPinned()" (pinnedChange)="railState.setPinned($event)">
        <ng-template #railTabContent let-tab>
          @if (railTabOf(tab); as active) {
            @switch (active.tab.type) {
              @case ('relation') {
                @if (active.mount; as mount) {
                  <ng-container [ngComponentOutlet]="mount.component" [ngComponentOutletInputs]="mount.inputs()" [ngComponentOutletInjector]="mount.injector ?? undefined" />
                }
              }
              @case ('custom') {
                @if (active.custom; as custom) {
                  <ng-container [ngComponentOutlet]="custom.component" [ngComponentOutletInputs]="custom.inputs()" />
                }
              }
              @default {
                @if (active.itemSections.length > 0) {
                  @for (item of active.itemSections; track item.section.path) {
                    <wb-section [path]="item.section.path" [title]="item.$title()" [columns]="item.section.columns" [layout]="item.section.layout" [readonly]="$page().section(item.section.path).$readonly()" [fields]="item.fields" [fieldGroups]="item.fieldGroups" [related]="item.section.related" [repeats]="item.repeats" [resolver]="item.resolver" [customRenderer]="item.customRenderer" [customRendererInputs]="item.customRendererInputs" [surfaceMode]="$surfaceMode()" [page]="$page()" />
                  }
                } @else {
                  <wb-financial-document-advanced-panel [fields]="active.fields" [page]="$page()" [surfaceMode]="$surfaceMode()" />
                }
              }
            }
          }
        </ng-template>
      </wb-surface-rail>
    }
  </ng-container>

</w-page>

<ng-template #documentContent>
      @if ($surfaceErrors().length > 0) {
        <div class="form-errors" role="alert">
          @for (err of $surfaceErrors(); track err.code) {
            <p>{{ err.message }}</p>
          }
        </div>
      }

      <!-- region: meta (heading always; schema-driven fields right-aligned when the schema declares any) -->
      <div class="meta-row">
        <h3 class="document-heading">
          {{ $documentHeading() }}
          @if ($canCopyRecord()) {
            <!-- Click copies the record's shareable URL; Shift copies its document number. -->
            <button type="button" class="document-copy" [attr.aria-label]="$copyRecordTitle()" [title]="$copyRecordTitle()" (click)="onCopyRecord($event)">
              <i [class]="$recordCopied() ? 'fa-regular fa-check' : 'fa-regular fa-link'"></i>
            </button>
          }
        </h3>
        @for (f of $metaFields(); track f.field.bind) {
          <div
            class="meta-slot"
            [class.meta-reference]="isReferenceBind(f.field.bind)"
            #referenceTip="tooltip"
            [tooltip]="referenceTooltip(f.field.bind)"
            [display]="false"
            placement="top"
            theme="light"
            [max-width]="360"
            [show-delay]="300"
            (mouseenter)="showReferenceTooltip(referenceTip, $event, f.field.bind)"
          >
            <label class="lbl" [class.required]="f.field.$required()">{{ f.metadata.displayName || f.field.bind }}</label>
            <wb-field [field]="f.field" [metadata]="f.metadata" [instance]="f.instance" [page]="$page()" [surfaceMode]="$surfaceMode()" errorDisplay="overlay" />
          </div>
        }
      </div>

      <!-- region: parties (lookup cards — any binds the schema places here) -->
      @if ($partyFields().length > 0 || $partyFieldGroups().length > 0) {
        <div class="top-row" [style.grid-template-columns]="'repeat(' + ($partyFields().length + $partyFieldGroups().length) + ', minmax(200px, 1fr))'">
          @for (f of $partyFields(); track f.field.bind; let i = $index) {
            @let party = partyView(f);
            <div class="party-slot">
              <label class="lbl">{{ party.label }}</label>
              <div class="party-card">
                <div class="avatar" [class.from]="i === 0" [class.to]="i !== 0">{{ initials(party.name) }}</div>
                <div class="party-body">
                  <div class="party-name">{{ party.name || ('Select ' + party.label) }}</div>
                  <div class="party-meta">
                    @if (party.phone) { <span>{{ party.phone }}</span> }
                    @if (party.email) { <span>{{ party.email }}</span> }
                  </div>
                </div>
                <div class="party-field">
                  <wb-field [field]="f.field" [metadata]="f.metadata" [instance]="f.instance" [page]="$page()" [surfaceMode]="$surfaceMode()" errorDisplay="overlay" />
                </div>
              </div>
            </div>
          }
          @for (item of $partyFieldGroups(); track item.group.path) {
            <wb-field-group [group]="item.group" [page]="$page()" [resolver]="item.resolver" [surfaceMode]="$surfaceMode()" />
          }
        </div>
      }

      <!-- region: delivery (one column per delivery section; wb-field owns each label) -->
      @if ($deliveryColumns().length > 0) {
        <div class="delivery-block">
          <h3 class="delivery-heading">Delivery</h3>
          <div class="delivery-columns">
            @for (column of $deliveryColumns(); track column.path) {
              <div class="delivery-column">
                @for (row of column.rows; track $index) {
                  <div class="delivery-row" [style.grid-template-columns]="'repeat(' + column.columns + ', minmax(0, 1fr))'">
                    @for (f of row; track f.field.bind) {
                      <div class="delivery-slot" [style.grid-column]="'span ' + (f.colspan ?? 1)">
                        <wb-field [field]="f.field" [metadata]="f.metadata" [instance]="f.instance" [page]="$page()" [surfaceMode]="$surfaceMode()" [customRenderer]="f.customRenderer" [customRendererInputs]="f.customRendererInputs" [rendererPresentation]="f.rendererPresentation" errorDisplay="overlay" />
                      </div>
                    }
                  </div>
                }
              </div>
            }
          </div>
        </div>
      }

      <!-- leftover .lines fields sit above the first section (Quote Top Section) -->
      @if ($linesIntroFields().length > 0) {
        <div class="lines-intro">
          @for (f of $linesIntroFields(); track f.field.bind) {
            <wb-field [field]="f.field" [metadata]="f.metadata" [instance]="f.instance" [page]="$page()" [surfaceMode]="$surfaceMode()" />
          }
        </div>
      }

      <!-- region: lines (first table / section-block repeat) -->
      @if ($linesRepeat(); as rep) {
        <div class="lines-wrap">
          @if (rep.layout === 'section-block') {
            <wb-repeat-sections [repeat]="rep" [page]="$page()" [resolver]="$handle().resolver" [surfaceMode]="$surfaceMode()" />
          } @else {
            <wb-repeat-table [config]="repeatTableConfig(rep)" [page]="$page()" [resolver]="$handle().resolver" [surfaceMode]="$surfaceMode()" />
          }
        </div>
      }

      <!-- region: notes + totals -->
      <div class="bottom-row" [class.align-line-actions]="$linesRepeat()?.allowRemove && !$readonly()">
        <div class="notes-side">
          @if ($notesFields().length > 0) {
            <div class="notes-box">
              @for (f of $notesFields(); track f.field.bind) {
                <wb-field [field]="f.field" [metadata]="f.metadata" [instance]="f.instance" [page]="$page()" [surfaceMode]="$surfaceMode()" />
              }
            </div>
          }
        </div>

        @if ($amountFields().length > 0 || $taxModeField()) {
          <aside class="price-summary" [class.pair]="!!$pairRows()">
            @if ($taxModeField(); as taxField) {
              <div class="ps-row ps-amounts">
                <span>Amounts are</span>
                @if ($readonly()) {
                  <span>{{ $taxMode() }}</span>
                } @else {
                  <wb-select
                    class="ps-select"
                    [items]="taxModeOptions"
                    bindLabel="label"
                    bindValue="value"
                    [clearable]="false"
                    [searchable]="false"
                    [ngModel]="$taxMode()"
                    (ngModelChange)="setTaxMode($event)"
                  />
                }
              </div>
            }
            @if ($pairRows(); as rows) {
              <div class="ps-row ps-pair-head">
                <span class="ps-label"></span>
                <span class="ps-value ps-buy">Buy</span>
                <span class="ps-value ps-sell">Sell</span>
              </div>
              @for (row of rows; track row.label) {
                <div class="ps-row" [class.total]="row.emphasis">
                  <span class="ps-label">{{ row.label }}</span>
                  <span class="ps-value ps-buy">
                    @if (row.buyBind; as buyBind) { {{ amountByBind(buyBind) | currencyFormat:'2' }} } @else { — }
                  </span>
                  <span class="ps-value ps-sell">
                    @if (row.sellBind; as sellBind) { {{ amountByBind(sellBind) | currencyFormat:'2' }} } @else { — }
                  </span>
                </div>
              }
              @if ($prepayment(); as prepayment) {
                <div class="ps-row ps-prepayment">
                  <span class="ps-label">Prepayment Required</span>
                  <span class="ps-value">
                    {{ prepayment.inc | currencyFormat:'2' }}
                    <span class="ps-note">(inc. {{ prepayment.tax | currencyFormat:'2' }} tax)</span>
                  </span>
                </div>
              }
              @if ($profitMetrics(); as pmm) {
                <div class="ps-row ps-pmm">
                  <span class="ps-label">Profit</span>
                  <span class="ps-value">{{ pmm.profit | currencyFormat:'2' }}</span>
                </div>
                <div class="ps-row ps-pmm">
                  <span class="ps-label">Margin %</span>
                  <span class="ps-value">{{ pmm.margin | number:'1.2-2' }}</span>
                </div>
                <div class="ps-row ps-pmm">
                  <span class="ps-label">Markup %</span>
                  <span class="ps-value">{{ pmm.markup | number:'1.2-2' }}</span>
                </div>
              }
            } @else {
              @for (f of $amountFields(); track f.field.bind) {
                <div class="ps-row" [class.total]="isTotalRow(f)">
                  <span class="ps-label">{{ f.metadata.displayName || f.field.bind }}</span>
                  <span class="ps-value">{{ amountValue(f) | currencyFormat:'2' }}</span>
                </div>
              }
            }
            <!-- Entity-contributed totals footer (`totalsFooter` capability); absent → nothing renders. -->
            @for (item of $totalsFooter(); track item.key) {
              <div class="ps-footer">
                <ng-container *ngComponentOutlet="item.component" />
              </div>
            }
          </aside>
        }
      </div>
</ng-template>

</div>
